import React from 'react'
import {
  Grid,
  Typography,
  Button,
  Card,
  CardMedia,
  Divider,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {Alert} from "@material-ui/lab"
import clsx from 'clsx'
import { Link } from 'react-router-dom'

const disallowedVariesAttributes = ['sku']
const disallowedVariesTypes = ['kvplist']

const useStyles = makeStyles({
  card: {
    display: 'flex',
    height: '80px',
    marginBottom: '8px',
    transition: 'all 0.2s ease',
  },
  cardHoverEffect: {
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    }
  },
  selected: {
    border: '2px solid #1976d2',
    backgroundColor: '#f3f9ff'
  },
  clickable: {
    cursor: 'pointer'
  },
  gridContainer: {
    padding: 0,
    '& .MuiGrid-item': {
      paddingLeft: 0
    }
  }
})

const ProductCard = ({ sku, name, image, variantName, isSelected, to = null, style = {} }) => {
  const classes = useStyles()
  const cardContent = (
    <Card 
      className={clsx(
        classes.card,
        isSelected && classes.selected,
        to && [classes.clickable, classes.cardHoverEffect]
      )}
      style={style}
    >
      <CardMedia
        component="img"
        image={image || "/placeholder-product.png"}
        alt={name || "Product"}
        style={{ 
          width: '80px',
          objectFit: 'contain', 
          backgroundColor: '#f5f5f5' 
        }}
      />
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        flex: 1,
        padding: '8px 16px',
        alignItems: 'flex-start',
        overflow: 'hidden'
      }}>
        <Typography variant="subtitle1" style={{ lineHeight: 1.2 }}>
          {sku || 'No SKU'}
        </Typography>
        <Typography
          variant="body2" color="textSecondary"
          style={{
            lineHeight: 1.2,
            textAlign: 'left',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {name || 'PRODUCT_NAME'} {variantName ? `| ${variantName}` : ''}
        </Typography>
      </div>
    </Card>
  )
  return to ? (
    <Link to={to} style={{ textDecoration: 'none' }} component="a">
      {cardContent}
    </Link>
  ) : cardContent
}

const ProductVariations = ({
  variesBy,
  formDisabled,
  catalog,
  product,
  initProduct,
  variations,
  setAddVariationDialog,
  setAddVariationSkuDialog,
  allowVariations,
  partnerId
}) => {
  const filteredVariesByOptions = variesBy.options
    .filter(
      (option, index, self) =>
        self.findIndex(t => t.label === option.label) === index
    )
    .filter(
      option => !disallowedVariesAttributes.includes(option.value)
    )
    .filter(field => !disallowedVariesTypes.includes(field.type));

  const allowAddingNewVariation = initProduct.Attributes.parent_sku && product.Attributes.parent_sku !== "";

  return (
    <>
      {allowVariations ? (
        <Grid container style={{ paddingLeft: 0 }}>
          <Grid item xs={12} style={{ paddingLeft: 0 }}>
            <Typography 
              variant="subtitle2" 
              color="textSecondary" 
              gutterBottom
              style={{ paddingLeft: 0, textAlign: 'left' }}
            >
              Current Variation
            </Typography>
            <ProductCard 
              sku={product.Attributes.sku} 
              name={product.Attributes.name}
              image={product.Attributes.image}
              variantName={product.Attributes.variant_name}
              isSelected={true}
            />
          </Grid>
          
          <Grid item xs={12} style={{ paddingLeft: 0 }}>
            <Typography 
              variant="subtitle2" 
              color="textSecondary" 
              gutterBottom
              style={{ paddingLeft: 0, textAlign: 'left' }}
            >
              Varies By
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px', paddingLeft: 0 }}>
              {filteredVariesByOptions.map((option, index) => {
                // Collect all values, flattening arrays
                const allValuesRaw = [
                  product.Attributes[option.value],
                  ...variations.map(v => v.Attributes[option.value])
                ].filter(Boolean);
                // Flatten arrays and normalize
                const allValues = allValuesRaw.flatMap(v => Array.isArray(v) ? v : [v]);
                // Deduplicate (case-insensitive, keep first occurrence's casing)
                const uniqueValues = Array.from(
                  allValues.reduce((map, val) => {
                    const lower = val.toString().toLowerCase();
                    if (!map.has(lower)) map.set(lower, val);
                    return map;
                  }, new Map())
                  .values()
                );
                // Sort alphabetically (case-insensitive)
                const sortedValues = uniqueValues.sort((a, b) =>
                  String(a).toLowerCase().localeCompare(String(b).toLowerCase())
                );
                return (
                  <div key={index} style={{ paddingLeft: 0 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom style={{ textAlign: 'left' }}>
                      {option.label}
                    </Typography>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {sortedValues.map((value, valueIndex) => {
                        const currentValue = product.Attributes[option.value];
                        const isSelected = Array.isArray(currentValue)
                          ? currentValue.map(v => v.toString().toLowerCase()).includes(value.toString().toLowerCase())
                          : currentValue && value && value.toString().toLowerCase() === currentValue.toString().toLowerCase();
                        return (
                          <div
                            key={valueIndex}
                            style={{
                              padding: '4px 12px',
                              borderRadius: '16px',
                              border: '1px solid #e0e0e0',
                              backgroundColor: isSelected ? '#1976d2' : 'transparent',
                              color: isSelected ? 'white' : 'inherit',
                              fontSize: '0.875rem'
                            }}
                          >
                            {value}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </Grid>
          
          <Grid item xs={12} style={{ paddingLeft: 0 }}>
            <Divider style={{ margin: '16px 0' }} />
            <Typography 
              variant="subtitle2" 
              color="textSecondary" 
              gutterBottom
              style={{ paddingLeft: 0, textAlign: 'left' }}
            >
              Other Variations
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {variations.map((variation, index) => {
                const variationId = variation.id || variation.Id || variation.Attributes?.id || variation.Attributes?.Id;
                let url = variationId ? `/productonboardingcenter/product?catalog=${catalog}&productId=${variationId}` : undefined;
                if(partnerId && url){
                  url += `&partnerId=${partnerId}`;
                }
                return (
                  <ProductCard
                    key={index}
                    sku={variation.Attributes.sku}
                    name={variation.Attributes.name}
                    image={variation.Attributes.image}
                    variantName={variation.Attributes.variant_name}
                    isSelected={false}
                    to={url}
                  />
                );
              })}
              {!formDisabled && (
                <Grid item xs={12}>
                  <Grid 
                    container 
                    spacing={2} 
                    style={{ 
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px'
                    }}
                  >
                    <Tooltip 
                      title={!allowAddingNewVariation ? "Please add a parent sku & save before adding a new variation" : ""}
                      placement="top"
                    >
                      <span>
                        <Button
                          variant="contained"
                          onClick={() => setAddVariationSkuDialog(true)}
                          style={{ whiteSpace: 'nowrap' }}
                          disabled={!allowAddingNewVariation}
                        >
                          Add new
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      onClick={() => setAddVariationDialog(true)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Manage
                    </Button>
                  </Grid>
                </Grid>
              )}
            </div>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">
          This product does not support variations. To enable variations, select an attribute set that includes the parent_sku and varies_by attributes.
        </Alert>
      )}
    </>
  )
}

export default React.memo(ProductVariations)